import React from "react";
import {
  FixedSizeList,
  type ListChildComponentProps,
  type ListOnItemsRenderedProps,
} from "react-window";

/** Bridges react-window + React 18 JSX typings in strict projects. */

const WindowRenderList = FixedSizeList as any;

export type { ListOnItemsRenderedProps } from "react-window";

/**
 * @template RowT - Type of each virtualized **row** (e.g. one screen row of items).
 * Pass `itemData` as `RowT[]` (e.g. chunked rows); each list index renders one row.
 */
export interface ReactWindowListProps<RowT> {
  height: number;
  itemCount: number;
  /** Same reference passed to `react-window` `itemData` — typically an array of rows. */
  itemData: RowT[];
  itemSize: number;
  width: number | string;
  renderOption: (params: { option: RowT; style: React.CSSProperties }) => React.ReactElement;
  className?: string;
  onItemsRendered?: (props: ListOnItemsRenderedProps) => void;
  overscanCount?: number;
}

const ReactWindowList = <RowT,>({
  height,
  itemCount,
  itemData,
  itemSize,
  width,
  renderOption,
  className = "",
  onItemsRendered,
  overscanCount,
}: ReactWindowListProps<RowT>) => {
  return (
    <WindowRenderList
      height={height}
      itemCount={itemCount}
      itemData={itemData}
      itemSize={itemSize}
      width={width}
      className={className}
      onItemsRendered={onItemsRendered}
      overscanCount={overscanCount}
    >
      {({ index, style, data }: ListChildComponentProps<RowT[]>) => {
        const rows = data;
        const option = rows[index];
        return renderOption({ option, style });
      }}
    </WindowRenderList>
  );
};

export default ReactWindowList;
